import { useApolloClient } from '@apollo/react-hooks';
import useAuthQuery from '../../../../hooks/useAuthQuery';
import {
  ReportOutageBySpListGuestRequest,
  ReportOutageBySpListRequest,
  ReportOutageResponse,
  OutageDetailsByZipCodeParams,
  OutageByCountyParams,
  OutageDetailsByCounty,
  OutageDetailsByZipCode,
  OutageEventDetails,
  OutageEventParams,
  SubmitCommPreferencesRequest,
  SubmitCommPreferencesResponse,
  AccountCustomer,
} from '../../../../__generated__/pge-types';
import {
  reportOutageBySpListMutation,
  reportOutageBySpListGuestMutation,
  getOutageDetailsByZipCodeQuery,
  getOutagesByCountyQuery,
  getOutageEventDetailsQuery,
  submitCommPreferenceMutation,
  getContactDetails,
} from './queries';

import useWrapWithLoader from '../../../../hooks/useWrapWithLoading';

export function useReportOutageApi() {
  const apolloClient = useApolloClient();

  const { wrapWithLoader } = useWrapWithLoader();

  const reportOutageForGuest = wrapWithLoader(
    async (payload: ReportOutageBySpListGuestRequest) => {
      const { data } = await apolloClient.mutate<{
        reportOutageBySpListGuest: ReportOutageResponse;
      }>({
        mutation: reportOutageBySpListGuestMutation,
        variables: {
          payload,
        },
      });
      return data;
    },
  );

  const reportOutageForLoggedInUser = async (
    payload: ReportOutageBySpListRequest,
  ) => {
    const { data } = await apolloClient.mutate<{
      reportOutageBySpListGuest: ReportOutageResponse;
    }>({
      mutation: reportOutageBySpListMutation,
      variables: {
        payload,
      },
    });
    return data;
  };

  const submitCommPreferenceForLoggedInUser = async (
    payload: SubmitCommPreferencesRequest,
  ) => {
    const { data } = await apolloClient.mutate<{
      submitCommPreference: SubmitCommPreferencesResponse;
    }>({
      mutation: submitCommPreferenceMutation,
      variables: {
        payload,
      },
    });
    return data;
  };

  const getOutageDetailsByZipCode = async (
    params: OutageDetailsByZipCodeParams,
  ) => {
    const { data } = await apolloClient.query<{
      getOutageDetailsByZipCode: OutageDetailsByZipCode;
    }>({
      query: getOutageDetailsByZipCodeQuery,
      variables: {
        params,
      },
      fetchPolicy: 'network-only',
    });
    return data;
  };

  const getOutagesByCounty = async (params: OutageByCountyParams) => {
    const { data, loading, errors } = await apolloClient.query<{
      getOutagesByCounty: OutageDetailsByCounty[];
    }>({
      query: getOutagesByCountyQuery,
      variables: {
        params,
      },
      fetchPolicy: 'network-only',
    });
    return {
      data: data.getOutagesByCounty,
      loading,
      errors,
    };
  };

  const getOutageEventDetails = async (params: OutageEventParams) => {
    const { data } = await apolloClient.query<{
      getOutageEventDetails: OutageEventDetails;
    }>({
      query: getOutageEventDetailsQuery,
      variables: {
        params,
      },
      fetchPolicy: 'network-only',
    });
    return data;
  };

  const fetchContactDetails = async () => {
    const { data } = await apolloClient.query<{
      getAccountInfo: AccountCustomer;
    }>({
      query: getContactDetails,
      variables: {
        params: {},
      },
    });
    return data.getAccountInfo;
  };

  return {
    reportOutageForGuest,
    reportOutageForLoggedInUser,
    getOutageDetailsByZipCode,
    getOutagesByCounty,
    getOutageEventDetails,
    submitCommPreferenceForLoggedInUser,
    fetchContactDetails,
  };
}
